import React, { useState, useEffect } from 'react';
import { X, Search, SlidersHorizontal, UserPlus, Filter, Calendar, Palmtree, IndianRupee, Receipt, Users } from 'lucide-react';
import { useUserContext } from '../contexts/UserContext';
import { supabase } from '../utils/supabase';
import { API_BASE_URL } from '../utils/apiConfig';
import toast from 'react-hot-toast';
import EmptyState from '../components/ui/EmptyState';
import TeamAttendanceTab from '../components/team/TeamAttendanceTab';
import TeamLeavesTab from '../components/team/TeamLeavesTab';
import TeamPayslipsTab from '../components/team/TeamPayslipsTab';
import TeamSalaryTab from '../components/team/TeamSalaryTab';
import StaffDetailDrawer from '../components/team/StaffDetailDrawer';
import StaffLevelsModal from '../components/team/StaffLevelsModal';
import { PROVIDER_ROLES } from '../components/team/staffApi';


interface TeamMember {
  id: string;
  userId: string;
  employeeId?: string | null;
  name: string;
  displayName?: string;
  email?: string | null;
  phoneNo?: string;
  role: 'trainer' | 'staff' | 'admin';
  status: 'active' | 'inactive';
  canLogin: boolean;
  gym: {
    id: string;
    name: string;
    displayName: string;
  };
  branches: Array<{
    id: string;
    name: string;
    displayName: string;
  }>;
  addedBy?: {
    id: string;
    name: string;
  };
  createdAt: string;
}

interface Branch {
  id: string;
  name: string;
  displayName: string;
  gym: {
    id: string;
    name: string;
    displayName: string;
  };
  canManage: boolean;
  role: 'owner' | 'admin';
}

type TeamTab = 'members' | 'attendance' | 'leaves' | 'salary' | 'payslips';
type TeamPageMode = 'team' | 'hrms';

const TeamPage: React.FC<{ mode?: TeamPageMode }> = ({ mode = 'team' }) => {
  const { hasRole, userContext } = useUserContext();
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedMember, setSelectedMember] = useState<TeamMember | null>(null);
  const [showRestoreModal, setShowRestoreModal] = useState(false);
  // A provider's Profile · Services · Availability, and the gym's levels.
  const [staffDetail, setStaffDetail] = useState<TeamMember | null>(null);
  const [showLevels, setShowLevels] = useState(false);
  const [memberToRestore, setMemberToRestore] = useState<any>(null);

  // Filters
  const [filters, setFilters] = useState({
    search: '',
    role: '',
    branchId: ''
  });
  const [draftFilters, setDraftFilters] = useState({
    role: '',
    branchId: ''
  });
  const [searchInput, setSearchInput] = useState('');
  const [showFiltersDrawer, setShowFiltersDrawer] = useState(false);
  const filterDrawerRef = React.useRef<HTMLDivElement>(null);

  // Close filter drawer on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (filterDrawerRef.current && !filterDrawerRef.current.contains(event.target as Node)) {
        setShowFiltersDrawer(false);
      }
    };

    if (showFiltersDrawer) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showFiltersDrawer]);

  const activeFilterCount = [filters.role, filters.branchId].filter(f => f !== '').length;

  const [formData, setFormData] = useState({
    name: '',
    displayName: '',
    email: '',
    phoneNo: '',
    role: 'trainer' as 'trainer' | 'staff' | 'admin',
    branchIds: [] as string[],
    canLogin: false
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const isOwner = hasRole('owner');
  const isAdmin = hasRole('admin');
  const currentPlanRole = userContext?.roles?.[0];
  const canUseStaffAttendance = currentPlanRole?.plan?.features?.staff_attendance === true;
  const showAttendanceTab = isOwner && canUseStaffAttendance;

  // For admins: derive the granted HRMS tab list from auth context
  const adminHrmsPermissions: string[] = isAdmin
    ? (userContext?.roles?.find(r => r.role === 'admin')?.hrmsPermissions ?? [])
    : [];

  const getAllowedHrmsTabs = (): TeamTab[] => {
    if (isOwner) {
      return showAttendanceTab
        ? ['attendance', 'leaves', 'salary', 'payslips']
        : ['leaves', 'salary', 'payslips'];
    }
    if (isAdmin) {
      const all: TeamTab[] = ['attendance', 'leaves', 'salary', 'payslips'];
      return all.filter(tab => {
        if (tab === 'attendance') return adminHrmsPermissions.includes('attendance') && canUseStaffAttendance;
        return adminHrmsPermissions.includes(tab);
      });
    }
    return [];
  };

  const allowedTabs: TeamTab[] = mode === 'hrms' ? getAllowedHrmsTabs() : ['members'];

  const [activeTab, setActiveTab] = useState<TeamTab>(() => {
    const hash = window.location.hash.replace('#', '');
    if (allowedTabs.includes(hash as TeamTab)) return hash as TeamTab;
    return allowedTabs[0] ?? ('leaves' as TeamTab);
  });

  // Update URL hash when tab changes
  useEffect(() => {
    window.location.hash = activeTab;
  }, [activeTab]);

  // Handle back/forward navigation — reject hashes not in allowedTabs
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.replace('#', '');
      if (hash && allowedTabs.includes(hash as TeamTab)) {
        setActiveTab(hash as TeamTab);
      } else if (allowedTabs.length > 0) {
        // Redirect to the first allowed tab if hash is not permitted
        setActiveTab(allowedTabs[0]);
      }
    };
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, [allowedTabs]);

  // Reset active tab when mode or permissions change
  useEffect(() => {
    if (!allowedTabs.includes(activeTab) && allowedTabs.length > 0) {
      setActiveTab(allowedTabs[0]);
    }
  }, [mode, showAttendanceTab, adminHrmsPermissions.join(',')]);
  const [showBranchDropdown, setShowBranchDropdown] = useState(false);


  const canManageTeam = hasRole('owner') || hasRole('admin');

  // Debug logging - only run once on mount
  useEffect(() => {
    console.log('TeamPage Debug:', {
      isOwner,
      canManageTeam,
      hasRoleOwner: hasRole('owner'),
      hasRoleAdmin: hasRole('admin'),
      userContext
    });
  }, []); // Empty dependency array - only run once

  // Validation functions
  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePhoneNumber = (phone: string): boolean => {
    const phoneRegex = /^[6-9]\d{9}$/;
    return phoneRegex.test(phone.replace(/\D/g, ''));
  };

  const validateField = (field: string, value: any, opts?: { emailRequired?: boolean }): string => {
    switch (field) {
      case 'name':
        if (!value.trim()) return 'Name is required';
        // Only allow letters and spaces
        if (!/^[a-zA-Z\s]+$/.test(value.trim())) {
          return 'Name should only contain letters and spaces';
        }
        break;
      case 'displayName':
        if (value && value.trim()) {
          // Only allow letters and spaces if displayName is provided
          if (!/^[a-zA-Z\s]+$/.test(value.trim())) {
            return 'Display name should only contain letters and spaces';
          }
        }
        break;
      case 'email':
        // Existing team members may not have a login email (e.g. phone-only trainers) - only
        // require it when adding a brand-new member; editing just validates format if provided.
        if (!value || !value.trim()) {
          return opts?.emailRequired ? 'Email is required' : '';
        }
        if (!validateEmail(value)) return 'Invalid email format';
        break;
      case 'phoneNo':
        // Phone number is mandatory for all team members
        if (!value || !value.trim()) {
          return 'Phone number is required';
        }
        if (!validatePhoneNumber(value)) {
          return 'Invalid phone number. Use 10-digit Indian mobile number.';
        }
        break;
      case 'role':
        if (!value) return 'Role is required';
        break;
    }
    return '';
  };

  const validateForm = (mode: 'create' | 'edit' = 'create'): { isValid: boolean; errors: { [key: string]: string } } => {
    const errors: { [key: string]: string } = {};

    errors.name = validateField('name', formData.name);
    errors.displayName = validateField('displayName', formData.displayName);
    errors.email = validateField('email', formData.email, { emailRequired: mode === 'create' });
    errors.phoneNo = validateField('phoneNo', formData.phoneNo);
    errors.role = validateField('role', formData.role);

    return {
      isValid: Object.values(errors).every(error => !error),
      errors
    };
  };



  // Fetch team members and branches
  const fetchData = async () => {
    try {
      setLoading(true);

      const { data: { session } } = await supabase.auth.getSession();
      console.log('Session:', session);
      if (!session?.access_token) {
        throw new Error('No access token');
      }

      const apiUrl = API_BASE_URL;
      console.log('Fetching team data from:', apiUrl);

      // Fetch branches for team management
      const branchesResponse = await fetch(`${apiUrl}/api/trainers/branches`, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        }
      });

      if (branchesResponse.ok) {
        const branchesData = await branchesResponse.json();
        setBranches(branchesData.data || []);
      }

      // Fetch team members with filters
      const queryParams = new URLSearchParams();
      if (filters.role) queryParams.append('role', filters.role);
      if (filters.branchId) queryParams.append('branchId', filters.branchId);
      if (filters.search) queryParams.append('search', filters.search);

      const teamResponse = await fetch(`${apiUrl}/api/trainers?${queryParams}`, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        }
      });

      if (teamResponse.ok) {
        const teamData = await teamResponse.json();
        console.log('Team data response:', teamData);
        setTeamMembers(teamData.data || []);
      } else if (teamResponse.status === 404) {
        console.log('No team members found (404)');
        setTeamMembers([]);
      } else {
        console.error('Team response error:', teamResponse.status, teamResponse.statusText);
        const errorText = await teamResponse.text();
        console.error('Error response:', errorText);
        throw new Error(`Failed to fetch team members: ${teamResponse.status}`);
      }

    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load team data');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setFilters(prev => ({ ...prev, search: searchInput }));
  };

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      setFilters(prev => ({ ...prev, search: searchInput }));
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [searchInput]);

  useEffect(() => {
    fetchData();
  }, [filters]);

  // Create team member
  const handleCreateMember = async () => {
    try {
      const validation = validateForm();
      if (!validation.isValid) {
        Object.values(validation.errors).forEach((error: string) => {
          if (error) toast.error(error);
        });
        return;
      }

      setIsSubmitting(true);
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error('No access token');
      }

      const apiUrl = API_BASE_URL;
      // Send branchIds array (empty array means all branches)
      // Include salary data if provided
      const payload: any = {
        name: formData.name,
        displayName: formData.displayName,
        email: formData.email,
        phoneNo: formData.phoneNo,
        role: formData.role,
        branchIds: formData.branchIds.length > 0 ? formData.branchIds : [],
        canLogin: formData.canLogin
      };

      const response = await fetch(`${apiUrl}/api/trainers`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (response.status === 409) {
        const errorData = await response.json();
        if (errorData.error === 'MEMBER_EXISTS_INACTIVE') {
          setMemberToRestore(errorData.data);
          setShowRestoreModal(true);
          setShowCreateModal(false);
          return;
        }
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create team member');
      }

      toast.success('Team member added successfully');
      setShowCreateModal(false);
      resetForm();
      setShowBranchDropdown(false);
      fetchData();
    } catch (error: any) {
      console.error('Error creating team member:', error);
      toast.error(error.message || 'Failed to create team member');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Update team member
  const handleUpdateMember = async () => {
    try {
      if (!selectedMember) return;

      const validation = validateForm('edit');
      if (!validation.isValid) {
        Object.values(validation.errors).forEach((error: string) => {
          if (error) toast.error(error);
        });
        return;
      }

      setIsSubmitting(true);
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        throw new Error('No access token');
      }

      const apiUrl = API_BASE_URL;
      // Prepare update payload (exclude branchIds from update - branches are managed separately).
      // Omit email entirely when blank so we don't try to overwrite an existing (or absent)
      // login email with an empty string - the backend leaves the field untouched when omitted.
      const payload: any = {
        name: formData.name,
        displayName: formData.displayName,
        ...(formData.email.trim() ? { email: formData.email.trim() } : {}),
        phoneNo: formData.phoneNo,
        role: formData.role,
        canLogin: formData.canLogin,
        branchIds: formData.branchIds
      };

      const response = await fetch(`${apiUrl}/api/trainers/${selectedMember.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update team member');
      }

      toast.success('Team member updated successfully');
      setShowEditModal(false);
      resetForm();
      fetchData();
    } catch (error: any) {
      console.error('Error updating team member:', error);
      toast.error(error.message || 'Failed to update team member');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRestoreMember = async () => {
    try {
      if (!memberToRestore) return;
      setIsSubmitting(true);
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) throw new Error('No access token');

      const response = await fetch(`${API_BASE_URL}/api/trainers/restore/${memberToRestore.id}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) throw new Error('Failed to restore member');

      toast.success('Team member restored successfully');
      setShowRestoreModal(false);
      setMemberToRestore(null);
      fetchData();
    } catch (error: any) {
      toast.error(error.message || 'Failed to restore member');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteMember = async () => {
    try {
      if (!selectedMember) return;
      setIsSubmitting(true);
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) throw new Error('No access token');

      const response = await fetch(`${API_BASE_URL}/api/trainers/${selectedMember.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) throw new Error('Failed to deactivate member');

      toast.success('Team member deactivated successfully');
      setShowDeleteModal(false);
      fetchData();
    } catch (error: any) {
      toast.error(error.message || 'Failed to deactivate member');
    } finally {
      setIsSubmitting(false);
    }
  };



  const resetFilters = () => {
    const defaultFilters = { role: '', branchId: '' };
    setDraftFilters(defaultFilters);
    setFilters(prev => ({ ...prev, ...defaultFilters }));
    setShowFiltersDrawer(false);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      displayName: '',
      email: '',
      phoneNo: '',
      role: 'trainer',
      branchIds: [],
      canLogin: false
    });
    setShowBranchDropdown(false);
  };





  // Close branch dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (showBranchDropdown && !target.closest('.branch-dropdown-container')) {
        setShowBranchDropdown(false);
      }
    };

    if (showBranchDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showBranchDropdown]);

  const openEditModal = async (member: TeamMember) => {
    // Prevent self-editing
    if (member.userId === userContext?.user.id) {
      toast.error('You cannot edit your own profile from the team tab. Please use your profile page instead.');
      return;
    }

    setSelectedMember(member);
    setFormData({
      name: member.name,
      displayName: member.displayName || '',
      email: member.email || '',
      phoneNo: member.phoneNo || '',
      role: member.role,
      branchIds: member.branches.map(b => b.id),
      canLogin: member.canLogin
    });

    setShowEditModal(true);
  };

  const openDeleteModal = (member: TeamMember) => {
    // Prevent self-deletion
    if (member.userId === userContext?.user.id) {
      toast.error('You cannot delete your own profile from the team tab.');
      return;
    }

    setSelectedMember(member);
    setShowDeleteModal(true);
  };





  const ALL_HRMS_TAB_DEFS = [
    { id: 'attendance', label: 'Attendance', icon: <Calendar className="w-4 h-4" /> },
    { id: 'leaves', label: 'Leaves', icon: <Palmtree className="w-4 h-4" /> },
    { id: 'salary', label: 'Salary', icon: <IndianRupee className="w-4 h-4" /> },
    { id: 'payslips', label: 'Payslips', icon: <Receipt className="w-4 h-4" /> },
  ];

  const tabs = mode === 'hrms'
    ? ALL_HRMS_TAB_DEFS.filter(t => allowedTabs.includes(t.id as TeamTab))
    : [{ id: 'members', label: 'Team Members', icon: <Users className="w-4 h-4" /> }];

  const renderTabContent = () => {
    switch (activeTab) {
      case 'members':
        return null;
      case 'attendance':
        return <TeamAttendanceTab branches={branches} />;
      case 'leaves':
        return <TeamLeavesTab teamMembers={teamMembers.map(m => ({ id: m.id, userId: m.userId, name: m.name, displayName: m.displayName }))} />;
      case 'salary':
        return <TeamSalaryTab teamMembers={teamMembers.map(m => ({ id: m.id, userId: m.userId, name: m.name, displayName: m.displayName, role: m.role }))} />;
      case 'payslips':
        return <TeamPayslipsTab teamMembers={teamMembers.map(m => ({ id: m.id, userId: m.userId, name: m.name, displayName: m.displayName }))} />;
      default:
        return null;
    }
  };

  // Guard: HRMS page is only accessible to owners and admins.
  // Non-owner/admin users should never reach here (sidebar hides it, but guard anyway).
  if (mode === 'hrms' && !isOwner && !isAdmin) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
        <div className="text-gray-400 text-6xl">🔒</div>
        <h2 className="text-xl font-bold text-gray-700">Access Denied</h2>
        <p className="text-gray-500 text-sm text-center max-w-sm">
          You do not have permission to access the HRMS module.
        </p>
      </div>
    );
  }

  // Guard: Admin with no HRMS tab permissions
  if (mode === 'hrms' && isAdmin && !isOwner && allowedTabs.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
        <div className="text-gray-400 text-6xl">🔒</div>
        <h2 className="text-xl font-bold text-gray-700">No HRMS Access</h2>
        <p className="text-gray-500 text-sm text-center max-w-sm">
          Your gym owner has not granted you access to any HRMS features.
          Please contact your gym owner to request access.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="bg-white px-6 py-4 rounded-2xl border border-gray-100 shadow-sm flex items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-black text-gray-900 tracking-tight leading-none uppercase">
            {mode === 'hrms' ? 'Human Resources' : 'Team Management'}
          </h1>
          <p className="text-[10px] font-black text-gray-400 mt-1 uppercase tracking-[0.2em]">
            {mode === 'hrms' ? 'Attendance & Payroll Management' : 'Staff Access & Branch Permissions'}
          </p>
        </div>

        {canManageTeam && mode !== 'hrms' && (branches[0]?.gym.id || teamMembers[0]?.gym.id) && (
          <button
            onClick={() => setShowLevels(true)}
            className="ml-auto px-4 py-2.5 border border-gray-200 hover:border-gray-400 text-gray-700 rounded-xl font-black text-xs uppercase tracking-widest transition-all active:scale-95"
          >
            Levels
          </button>
        )}
        {canManageTeam && mode !== 'hrms' && (
          <button
            onClick={() => { resetForm(); setShowCreateModal(true); }}
            className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-xl font-black text-xs uppercase tracking-widest transition-all active:scale-95 shadow-lg shadow-blue-100 flex items-center justify-center gap-2"
          >
            <UserPlus className="w-4 h-4" />
            <span>Add Member</span>
          </button>
        )}
      </div>
      {/* Tabs - only show if there are multiple tabs (HRMS mode) */}
      {tabs.length > 1 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="border-b border-gray-200">
            <div className="flex space-x-1 px-4 overflow-x-auto">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as any)}
                  className={`flex items-center gap-2 px-4 py-2.5 transition-all duration-300 border-b-2 whitespace-nowrap group ${activeTab === tab.id
                    ? 'border-blue-600 text-blue-600 bg-blue-50/50'
                    : 'border-transparent text-gray-400 hover:text-gray-600 hover:bg-gray-50/50'
                    }`}
                >
                  <div className={`transition-transform duration-300 ${activeTab === tab.id ? 'scale-110' : 'group-hover:scale-110'}`}>
                    {React.cloneElement(tab.icon as React.ReactElement, { className: 'w-4 h-4' })}
                  </div>
                  <span className="text-[11px] font-black uppercase tracking-[0.15em] leading-none">
                    {tab.label}
                  </span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Tab Content */}
      <div>
        {mode === 'team' && activeTab === 'members' ? (
          <>
            {renderTabContent()}

            {/* Search and Filters Header Row */}
            <div className="flex items-center justify-between gap-2 mb-6 bg-white p-2 rounded-2xl shadow-sm border border-gray-100">
              {/* Search Input */}
              <div className="flex-1 relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search className="h-4 w-4 text-gray-400" />
                </div>
                <input
                  type="text"
                  value={searchInput}
                  onChange={(e) => {
                    setSearchInput(e.target.value);
                  }}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  placeholder="Search team..."
                  className="block w-full pl-9 pr-10 py-2.5 border border-transparent rounded-xl bg-gray-50 focus:bg-white focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm transition-all shadow-inner"
                />
                {searchInput && (
                  <button
                    onClick={() => {
                      setSearchInput('');
                    }}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 transition-colors group"
                  >
                    <X className="h-4 w-4 group-hover:scale-110 transition-transform" />
                  </button>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex items-center gap-1.5 sm:gap-2">
                <button
                  onClick={handleSearch}
                  className="p-2.5 bg-gray-50 hover:bg-gray-100 text-gray-700 rounded-xl transition-all active:scale-95 border border-gray-100 shadow-sm sm:hidden"
                  title="Search"
                >
                  <Search className="w-5 h-5" />
                </button>

                <button
                  onClick={() => {
                    setDraftFilters({ role: filters.role, branchId: filters.branchId });
                    setShowFiltersDrawer(true);
                  }}
                  className={`relative p-2.5 rounded-xl transition-all active:scale-95 border shadow-sm flex items-center gap-2 ${activeFilterCount > 0
                    ? 'bg-blue-50 border-blue-200 text-blue-600'
                    : 'bg-gray-50 border-gray-100 text-gray-700 hover:bg-gray-100'
                    }`}
                  title="Filters"
                >
                  <SlidersHorizontal className="w-5 h-5" />
                  {activeFilterCount > 0 && (
                    <span className="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-blue-600 text-[10px] font-bold text-white ring-2 ring-white">
                      {activeFilterCount}
                    </span>
                  )}
                  <span className="hidden md:inline text-sm font-semibold">Filters</span>
                </button>
              </div>
            </div>
            <div className="flex items-center justify-between -mt-4 mb-2 px-1">
              {teamMembers.length > 0 && (
                <span className="text-sm font-semibold text-gray-500">
                  <span className="text-gray-900">{teamMembers.length}</span> staff members
                </span>
              )}
            </div>

            {/* Filter Drawer / Sidebar Overlay */}
            {showFiltersDrawer && (
              <div className="fixed inset-0 z-[60] flex justify-end overflow-hidden bg-black/40 backdrop-blur-sm animate-in fade-in duration-200">
                <div
                  ref={filterDrawerRef}
                  className="w-full max-w-[320px] bg-white h-full shadow-2xl flex flex-col animate-in slide-in-from-right duration-300"
                >
                  <div className="p-6 border-b border-gray-100 flex items-center justify-between bg-gray-50/50">
                    <div className="flex items-center gap-2">
                      <Filter className="w-5 h-5 text-gray-900" />
                      <h2 className="text-xl font-bold text-gray-900">Filters</h2>
                    </div>
                    <button
                      onClick={() => setShowFiltersDrawer(false)}
                      className="p-2 hover:bg-gray-200 rounded-full transition-colors"
                      title="Close"
                    >
                      <X className="w-5 h-5 text-gray-500" />
                    </button>
                  </div>

                  <div className="flex-1 overflow-y-auto p-6 space-y-8">
                    {/* Role Filter */}
                    <div className="space-y-4">
                      <h3 className="text-xs font-black text-gray-400 uppercase tracking-widest">Team Role</h3>
                      <div className="grid grid-cols-1 gap-2">
                        {['all', 'admin', 'trainer', 'staff'].map((r) => (
                          <button
                            key={r}
                            onClick={() => setDraftFilters(prev => ({ ...prev, role: r === 'all' ? '' : r }))}
                            className={`px-4 py-3 text-sm font-bold rounded-xl transition-all border text-left flex items-center justify-between ${(r === 'all' ? draftFilters.role === '' : draftFilters.role === r)
                              ? 'bg-blue-50 border-blue-200 text-blue-600 ring-2 ring-blue-500/10'
                              : 'bg-gray-50 border-gray-200 text-gray-600 hover:border-blue-400'
                              }`}
                          >
                            <span className="capitalize">{r}</span>
                            {(r === 'all' ? draftFilters.role === '' : draftFilters.role === r) && (
                              <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                            )}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Branch Filter */}
                    <div className="space-y-4">
                      <h3 className="text-xs font-black text-gray-400 uppercase tracking-widest">Branch Access</h3>
                      <select
                        value={draftFilters.branchId}
                        onChange={(e) => setDraftFilters(prev => ({ ...prev, branchId: e.target.value }))}
                        className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm font-medium"
                      >
                        <option value="">All Branches</option>
                        {branches.map((branch) => (
                          <option key={branch.id} value={branch.id}>
                            {branch.displayName}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div className="p-6 bg-gray-50/50 border-t border-gray-100 flex gap-3">
                    <button
                      onClick={resetFilters}
                      className="flex-1 py-3 text-sm font-bold text-gray-600 hover:text-gray-900 transition-colors"
                    >
                      Reset All
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Team Members List */}
            {loading ? (
              <div className="flex flex-col items-center justify-center py-24 bg-white/50 backdrop-blur-xl rounded-[40px] border border-gray-100 min-h-[460px]">
                <div className="w-12 h-12 border-4 border-blue-600/20 border-t-blue-600 rounded-full animate-spin mb-4" />
                <p className="text-[10px] text-gray-400 font-black uppercase tracking-[0.2em] animate-pulse">Synchronizing team</p>
              </div>
            ) : teamMembers.length === 0 ? (
              <EmptyState
                icon={<UserPlus className="w-16 h-16 text-gray-200" />}
                title="No members recruited"
                description={canManageTeam ? "Your squad is empty. Start adding trainers and staff members to manage your gym." : "No team members are currently assigned to your view."}
              />
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {teamMembers.map((member) => {
                  const isMemberOwner = isOwner && member.userId === userContext?.user.id;
                  const roleConfig: Record<string, { color: string, bg: string, border: string, text: string }> = {
                    admin: { color: 'from-purple-500 to-indigo-600', bg: 'bg-purple-50', border: 'border-purple-100', text: 'text-purple-600' },
                    trainer: { color: 'from-emerald-500 to-teal-600', bg: 'bg-emerald-50', border: 'border-emerald-100', text: 'text-emerald-600' },
                    staff: { color: 'from-blue-500 to-sky-600', bg: 'bg-blue-50', border: 'border-blue-100', text: 'text-blue-600' }
                  };
                  const currentRole = roleConfig[member.role] || roleConfig.staff;

                  return (
                    <div
                      key={member.id}
                      className={`group relative bg-white rounded-[24px] border transition-all duration-300 hover:shadow-xl overflow-hidden ${
                        isMemberOwner ? 'border-amber-200 ring-4 ring-amber-500/5' : 'border-zinc-100'
                      }`}
                    >
                      {/* Subdued Header Strip */}
                      <div className={`h-16 bg-gradient-to-br opacity-[0.03] absolute top-0 left-0 right-0 ${currentRole.color}`} />
                      
                      <div className="p-6 relative">
                        <div className="flex justify-between items-start mb-6">
                          <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-white font-black text-xl shadow-lg transform group-hover:rotate-3 transition-transform duration-500 bg-gradient-to-br ${currentRole.color}`}>
                            {(member.displayName || member.name).charAt(0).toUpperCase()}
                          </div>
                          
                          <div className="flex flex-col items-end gap-2">
                             {isMemberOwner ? (
                               <span className="px-4 py-1.5 rounded-full text-[10px] font-black bg-amber-100 text-amber-700 border border-amber-200 uppercase tracking-widest">
                                  YOU · OWNER
                               </span>
                             ) : (
                               <>
                                 <span className={`px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest border ${currentRole.bg} ${currentRole.text} ${currentRole.border}`}>
                                   {member.role}
                                 </span>
                                 {member.status === 'inactive' && (
                                   <span className="px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest bg-red-50 text-red-500 border border-red-100">
                                     Inactive
                                   </span>
                                 )}
                               </>
                             )}
                          </div>
                        </div>

                        <div className="space-y-0.5 mb-5">
                           <h3 className="text-xl font-black text-zinc-900 leading-tight group-hover:text-blue-600 transition-colors truncate" title={member.displayName || member.name}>
                             {member.displayName || member.name}
                           </h3>
                           <p className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest">
                             {member.employeeId ? `Staff ID: ${member.employeeId}` : 'Team Member'}
                           </p>
                        </div>

                        <div className="space-y-3 py-4 border-y border-zinc-50 mb-5">
                           {!isMemberOwner && member.email && (
                             <div className="flex items-center gap-3 text-zinc-400 hover:text-zinc-900 transition-colors group/link cursor-default">
                                <div className="w-8 h-8 rounded-lg bg-zinc-50 flex items-center justify-center group-hover/link:bg-blue-50 group-hover/link:text-blue-600 transition-all">
                                   <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
                                </div>
                                <span className="text-xs font-bold truncate flex-1">{member.email}</span>
                             </div>
                           )}
                           {!isMemberOwner && member.phoneNo && (
                             <div className="flex items-center gap-3 text-zinc-400 hover:text-zinc-900 transition-colors group/link cursor-default">
                                <div className="w-8 h-8 rounded-lg bg-zinc-50 flex items-center justify-center group-hover/link:bg-green-50 group-hover/link:text-green-600 transition-all">
                                   <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" /></svg>
                                </div>
                                <span className="text-xs font-bold flex-1">{member.phoneNo}</span>
                             </div>
                           )}
                           {!isMemberOwner && (
                             <div className="flex items-center gap-3 text-zinc-400 hover:text-zinc-900 transition-colors group/link cursor-default">
                                <div className="w-8 h-8 rounded-lg bg-zinc-50 flex items-center justify-center group-hover/link:bg-purple-50 group-hover/link:text-purple-600 transition-all">
                                   <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/></svg>
                                </div>
                                <span className="text-xs font-bold truncate flex-1">
                                   {member.branches.length === 0 ? "No branch access" : member.branches.length === 1 ? member.branches[0].displayName : `${member.branches.length} Branches`}
                                </span>
                             </div>
                           )}
                           {isMemberOwner && (
                             <div className="py-6 text-center">
                                <p className="text-[10px] font-black text-amber-600 uppercase tracking-widest bg-amber-50 px-4 py-2 rounded-xl border border-amber-100 inline-block">
                                  Owner Account
                                </p>
                             </div>
                           )}
                        </div>

                        {canManageTeam && PROVIDER_ROLES.includes(member.role as string) && member.status === 'active' && (
                          <button
                            onClick={() => setStaffDetail(member)}
                            className="w-full mb-2 py-3 bg-lime-400 hover:bg-lime-300 text-zinc-900 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all transform active:scale-95"
                          >
                            Profile · Services · Hours
                          </button>
                        )}
                        {canManageTeam && !isMemberOwner && (
                          <div className="flex gap-2">
                             <button
                               onClick={() => openEditModal(member)}
                               className="flex-[3] py-3 bg-zinc-900 hover:bg-black text-white rounded-xl text-[10px] font-black uppercase tracking-widest transition-all transform active:scale-95 flex items-center justify-center gap-2"
                             >
                               Edit Member
                             </button>
                             {member.status === 'active' && (
                               <button
                                 onClick={() => openDeleteModal(member)}
                                 className="flex-1 flex items-center justify-center text-red-500 bg-red-50 hover:bg-red-100 rounded-2xl transition-all active:scale-90"
                                 title="Revoke Access"
                               >
                                 <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                               </button>
                             )}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {staffDetail && <StaffDetailDrawer member={staffDetail} onClose={() => setStaffDetail(null)} />}
            {showLevels && (branches[0]?.gym.id || teamMembers[0]?.gym.id) && (
              <StaffLevelsModal gymId={(branches[0]?.gym.id || teamMembers[0]?.gym.id)!} onClose={() => setShowLevels(false)} />
            )}

            {/* Restore Member Dialog */}
            {showRestoreModal && memberToRestore && (
              <div className="fixed inset-0 bg-black/70 backdrop-blur-xl flex items-center justify-center z-[80] p-4 animate-in fade-in duration-500">
                <div className="bg-white rounded-[48px] shadow-3xl w-full max-w-md overflow-hidden animate-in zoom-in-95 duration-500">
                   <div className="h-1.5 bg-blue-600" />
                   <div className="p-10">
                     <div className="w-24 h-24 bg-blue-50 rounded-[32px] flex items-center justify-center mb-8 mx-auto ring-8 ring-blue-500/5">
                        <UserPlus className="w-10 h-10 text-blue-600" />
                     </div>
                     <h2 className="text-2xl font-black text-center text-zinc-900 mb-2">Restore Member?</h2>
                     <p className="text-zinc-400 text-center text-[10px] font-bold mb-8 uppercase tracking-widest px-4">
                        AN INACTIVE ACCOUNT FOR <span className="text-blue-600">{memberToRestore.name}</span> WAS FOUND.
                     </p>

                     <div className="bg-zinc-50 rounded-2xl p-5 mb-8 space-y-3">
                        <div className="flex items-center justify-between border-b border-zinc-100 pb-2.5">
                          <span className="text-[9px] font-black text-zinc-300 uppercase tracking-widest">Email</span>
                          <span className="text-[11px] font-black text-zinc-900">{memberToRestore.email}</span>
                        </div>
                        <div className="flex items-center justify-between border-b border-zinc-100 pb-2.5">
                          <span className="text-[9px] font-black text-zinc-300 uppercase tracking-widest">Phone</span>
                          <span className="text-[11px] font-black text-zinc-900">{memberToRestore.phoneNo}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-[9px] font-black text-zinc-300 uppercase tracking-widest">Previous Role</span>
                          <span className="text-[11px] font-black text-blue-600 uppercase tracking-widest">{memberToRestore.role}</span>
                        </div>
                     </div>

                     <div className="grid grid-cols-1 gap-3">
                        <button
                          onClick={handleRestoreMember}
                          disabled={isSubmitting}
                          className="w-full py-4 bg-blue-600 hover:bg-blue-700 text-white rounded-2xl font-black uppercase tracking-widest transition-all transform active:scale-95 shadow-lg shadow-blue-500/20 disabled:opacity-50"
                        >
                          {isSubmitting ? 'Restoring...' : 'Restore Now'}
                        </button>
                        <button
                          onClick={() => { setShowRestoreModal(false); setMemberToRestore(null); }}
                          className="w-full py-4 bg-transparent text-zinc-400 hover:text-zinc-900 font-bold text-xs uppercase tracking-widest transition-colors"
                        >
                          Cancel
                        </button>
                     </div>
                     <p className="mt-6 text-[8px] text-center text-zinc-400 font-bold uppercase tracking-widest leading-relaxed">
                        Details can be edited after restore.
                     </p>
                   </div>
                </div>
              </div>
            )}

            {/* Add Team Member Modal */}
            {showCreateModal && (
              <div className="fixed inset-0 bg-black/70 backdrop-blur-xl flex items-start justify-center z-[80] p-4 overflow-y-auto animate-in fade-in duration-500">
                <div className="bg-white rounded-[40px] shadow-3xl w-full max-w-3xl my-6 animate-in slide-in-from-bottom-12 duration-700 overflow-hidden">
                   <div className="p-8 border-b border-zinc-50 flex justify-between items-center bg-zinc-50/30">
                      <div>
                        <h2 className="text-3xl font-black text-zinc-900 tracking-tighter mb-1">Add a New Member</h2>
                        <p className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest">Register a new staff or trainer</p>
                      </div>
                      <button onClick={() => setShowCreateModal(false)} className="w-12 h-12 flex items-center justify-center rounded-xl bg-white border border-zinc-100 text-zinc-400 hover:text-zinc-900 transition-all">
                        <X className="w-5 h-5" />
                      </button>
                   </div>

                   <form onSubmit={(e) => { e.preventDefault(); handleCreateMember(); }} className="p-10 lg:p-14">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
                        {/* Identity Pillar */}
                        <div className="space-y-8">
                           <div className="space-y-4">
                             <div className="flex items-center gap-2 mb-1">
                                <div className="w-1 h-4 bg-blue-600 rounded-full" />
                                <label className="text-[10px] font-black text-zinc-900 uppercase tracking-widest">Personal Details</label>
                             </div>
                             <input
                                type="text"
                                placeholder="Full Name"
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                className="w-full px-8 py-5 bg-gray-50 border-none rounded-[24px] focus:ring-4 focus:ring-blue-600/5 text-gray-900 font-black placeholder:text-gray-300 transition-all"
                                required
                             />
                              <input
                                type="text"
                                placeholder="Signature Alias (Optional)"
                                value={formData.displayName}
                                onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
                                className="w-full px-8 py-5 bg-gray-50 border-none rounded-[24px] focus:ring-4 focus:ring-blue-600/5 text-gray-900 font-black placeholder:text-gray-300 transition-all"
                             />
                           </div>
                             <div className="space-y-4">
                              <div className="flex items-center gap-2 mb-1">
                                 <div className="w-1 h-4 bg-green-500 rounded-full" />
                                 <label className="text-[10px] font-black text-zinc-900 uppercase tracking-widest">Contact Information</label>
                              </div>
                              <input
                                 type="email"
                                 placeholder="Email Address"
                                 value={formData.email}
                                 onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                 className="w-full px-6 py-4 bg-zinc-50 border-none rounded-2xl focus:ring-4 focus:ring-blue-600/5 text-zinc-900 font-black placeholder:text-zinc-300 transition-all"
                                 required
                              />
                              <input
                                 type="tel"
                                 placeholder="Phone Number"
                                 value={formData.phoneNo}
                                 onChange={(e) => setFormData({ ...formData, phoneNo: e.target.value })}
                                 className="w-full px-6 py-4 bg-zinc-50 border-none rounded-2xl focus:ring-4 focus:ring-blue-600/5 text-zinc-900 font-black placeholder:text-zinc-300 transition-all"
                                 required
                              />
                           </div>

                        </div>

                        {/* Authority Pillar */}
                        <div className="space-y-8">
                           <div className="space-y-4">
                             <div className="flex items-center gap-2 mb-1">
                                <div className="w-1 h-4 bg-purple-600 rounded-full" />
                                <label className="text-[10px] font-black text-zinc-900 uppercase tracking-widest">Role & Access</label>
                             </div>
                             <div className="grid grid-cols-1 gap-3">
                               {['trainer', 'staff', 'admin'].map(r => (
                                 <button
                                   key={r}
                                   type="button"
                                   onClick={() => setFormData({ ...formData, role: r as any })}
                                   className={`group flex items-center justify-between px-6 py-4 rounded-2xl border-2 transition-all duration-300 ${
                                     formData.role === r ? 'bg-zinc-900 border-zinc-900 text-white shadow-lg' : 'bg-white border-zinc-100 text-zinc-400 hover:border-zinc-200'
                                   }`}
                                 >
                                   <span className="text-[10px] font-black uppercase tracking-widest">{r}</span>
                                   {formData.role === r && <div className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-pulse" />}
                                 </button>
                               ))}
                             </div>
                           </div>

                           <div className="p-6 bg-zinc-900 rounded-[24px] text-white overflow-hidden relative group">
                              <div className="absolute inset-0 bg-gradient-to-br from-blue-600/10 to-purple-600/10 opacity-30" />
                              <div className="relative z-10">
                                <div className="flex items-center justify-between mb-3">
                                  <span className="text-[10px] font-black uppercase tracking-widest text-blue-400">Login Access</span>
                                  <div 
                                    onClick={() => setFormData({ ...formData, canLogin: !formData.canLogin })}
                                    className={`w-14 h-8 rounded-full p-1 cursor-pointer transition-colors ${formData.canLogin ? 'bg-blue-500' : 'bg-gray-700'}`}
                                  >
                                    <div className={`w-6 h-6 bg-white rounded-full shadow-lg transform transition-transform duration-300 ${formData.canLogin ? 'translate-x-6' : 'translate-x-0'}`} />
                                  </div>
                                </div>
                                <p className="text-[10px] font-bold text-gray-400 leading-relaxed uppercase tracking-wider">
                                  Allow this member to authenticate and access the dashboard cloud systems.
                                </p>
                              </div>
                           </div>
                        </div>
                      </div>

                      <div className="mt-16 space-y-6">
                         <div className="flex items-center gap-2 mb-4">
                            <div className="w-1.5 h-6 bg-amber-500 rounded-full" />
                            <label className="text-[10px] font-black text-zinc-900 uppercase tracking-widest">Assigned Branches</label>
                         </div>
                         <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                            {branches.map(branch => (
                              <label key={branch.id} className={`flex items-center px-6 py-4 rounded-2xl border-2 cursor-pointer transition-all duration-300 ${formData.branchIds.includes(branch.id) ? 'bg-blue-50 border-blue-600 text-blue-900' : 'bg-gray-50 border-transparent text-gray-500 hover:border-gray-200'}`}>
                                 <input
                                   type="checkbox"
                                   checked={formData.branchIds.includes(branch.id)}
                                   onChange={(e) => {
                                     const newIds = e.target.checked
                                       ? [...formData.branchIds, branch.id]
                                       : formData.branchIds.filter(id => id !== branch.id);
                                     setFormData({ ...formData, branchIds: newIds });
                                   }}
                                   className="hidden"
                                 />
                                 <span className="text-[10px] font-black uppercase tracking-widest truncate">{branch.displayName}</span>
                              </label>
                            ))}
                         </div>
                         <p className="text-[9px] font-black text-zinc-300 uppercase tracking-widest text-center italic mt-4">SELECT THE BRANCHES THIS MEMBER CAN ACCESS · NONE = NO ACCESS</p>
                      </div>

                      <div className="mt-16 flex flex-col sm:flex-row gap-6">
                        <button
                          type="button"
                          onClick={() => setShowCreateModal(false)}
                          className="flex-1 py-6 text-xs font-black text-gray-400 hover:text-gray-900 uppercase tracking-[0.2em] transition-colors order-2 sm:order-1"
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          disabled={isSubmitting}
                          className="flex-[2] py-6 bg-blue-600 hover:bg-blue-700 text-white rounded-[24px] text-xs font-black uppercase tracking-[0.3em] transition-all shadow-2xl shadow-blue-500/30 active:scale-95 disabled:opacity-50 order-1 sm:order-2"
                        >
                          {isSubmitting ? 'Saving...' : 'Add Member'}
                        </button>
                      </div>
                   </form>
                </div>
              </div>
            )}
            {/* Edit Team Member Modal - Modernized */}
            {showEditModal && selectedMember && (
              <div className="fixed inset-0 bg-black/70 backdrop-blur-xl flex items-center justify-center z-[80] p-4 animate-in fade-in duration-300">
                <div className="bg-white rounded-[32px] shadow-3xl w-full max-w-lg max-h-[88vh] flex flex-col animate-in slide-in-from-bottom-8 duration-500 overflow-hidden">
                   <div className="px-6 py-5 border-b border-zinc-50 flex justify-between items-center bg-zinc-50/30 shrink-0">
                      <div>
                        <h2 className="text-xl font-black text-zinc-900 tracking-tight mb-0.5">Edit Profile</h2>
                        <p className="text-[10px] font-bold text-blue-600 uppercase tracking-widest">{selectedMember.name}</p>
                      </div>
                      <button onClick={() => setShowEditModal(false)} className="w-9 h-9 flex items-center justify-center rounded-xl bg-white border border-zinc-100 text-zinc-400 hover:text-zinc-900 transition-all shrink-0">
                        <X className="w-4 h-4" />
                      </button>
                   </div>

                   <form onSubmit={(e) => { e.preventDefault(); handleUpdateMember(); }} className="flex flex-col flex-1 overflow-hidden">
                      <div className="px-6 py-6 space-y-6 overflow-y-auto flex-1">
                         <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div className="space-y-1.5">
                               <label className="text-[10px] font-black text-zinc-400 uppercase tracking-widest ml-1">Full Name</label>
                               <input type="text" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className="w-full px-4 py-3 bg-gray-50 border-none rounded-2xl focus:ring-4 focus:ring-blue-600/5 text-gray-900 font-bold text-sm" required />
                            </div>
                            <div className="space-y-1.5">
                               <label className="text-[10px] font-black text-zinc-400 uppercase tracking-widest ml-1">Display Name</label>
                               <input type="text" value={formData.displayName} onChange={(e) => setFormData({ ...formData, displayName: e.target.value })} className="w-full px-4 py-3 bg-gray-50 border-none rounded-2xl focus:ring-4 focus:ring-blue-600/5 text-gray-900 font-bold text-sm" />
                            </div>
                         </div>

                         <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div className="space-y-1.5">
                               <label className="text-[10px] font-black text-zinc-400 uppercase tracking-widest ml-1">Email</label>
                               <input
                                 type="email"
                                 value={formData.email}
                                 onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                 placeholder="No login email set"
                                 className="w-full px-4 py-3 bg-gray-50 border-none rounded-2xl focus:ring-4 focus:ring-blue-600/5 text-gray-900 font-bold text-sm placeholder:text-zinc-300 placeholder:font-medium"
                               />
                               {!formData.email && (
                                 <p className="text-[9px] font-bold text-zinc-300 uppercase tracking-wider ml-1">Optional for phone-only members</p>
                               )}
                            </div>
                            <div className="space-y-1.5">
                               <label className="text-[10px] font-black text-zinc-400 uppercase tracking-widest ml-1">Mobile</label>
                               <input type="tel" value={formData.phoneNo} onChange={(e) => setFormData({ ...formData, phoneNo: e.target.value })} className="w-full px-4 py-3 bg-gray-50 border-none rounded-2xl focus:ring-4 focus:ring-blue-600/5 text-gray-900 font-bold text-sm" required />
                            </div>
                         </div>
                          <div className="space-y-2.5">
                            <label className="text-[10px] font-black text-zinc-400 uppercase tracking-widest ml-1">Member Role</label>
                            <div className="grid grid-cols-3 gap-2.5">
                               {['trainer', 'staff', 'admin'].map(r => (
                                 <button
                                   key={r}
                                   type="button"
                                   onClick={() => {
                                     const newRole = r as any;
                                     setFormData({
                                       ...formData,
                                       role: newRole,
                                       canLogin: newRole === 'admin' ? formData.canLogin : false
                                     });
                                   }}
                                   className={`py-3 rounded-2xl border-2 text-[10px] font-black uppercase tracking-widest transition-all ${
                                     formData.role === r ? 'bg-zinc-900 border-zinc-900 text-white shadow-lg' : 'bg-white border-zinc-100 text-zinc-400 hover:border-zinc-200'
                                   }`}
                                 >
                                   {r}
                                 </button>
                               ))}
                            </div>
                         </div>

                         {formData.role === 'admin' && (
                           <div className="p-4 bg-zinc-900 rounded-2xl text-white overflow-hidden relative group">
                              <div className="absolute inset-0 bg-gradient-to-br from-blue-600/10 to-purple-600/10 opacity-30" />
                              <div className="relative z-10">
                                <div className="flex items-center justify-between mb-2">
                                  <span className="text-[10px] font-black uppercase tracking-widest text-blue-400">Login Access</span>
                                  <div
                                    onClick={() => setFormData({ ...formData, canLogin: !formData.canLogin })}
                                    className={`w-12 h-7 rounded-full p-1 cursor-pointer transition-colors ${formData.canLogin ? 'bg-blue-500' : 'bg-zinc-700'}`}
                                  >
                                    <div className={`w-5 h-5 bg-white rounded-full shadow-lg transform transition-transform duration-300 ${formData.canLogin ? 'translate-x-5' : 'translate-x-0'}`} />
                                  </div>
                                </div>
                                <p className="text-[10px] font-bold text-zinc-400 leading-relaxed uppercase tracking-wider">
                                  Allow this member to authenticate and access the dashboard cloud systems.
                                </p>
                              </div>
                           </div>
                         )}

                         <div className="space-y-3 pt-2">
                            <div className="flex items-center gap-2">
                               <div className="w-1.5 h-5 bg-amber-500 rounded-full" />
                               <label className="text-[10px] font-black text-zinc-900 uppercase tracking-widest">Assigned Branches</label>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                               {branches.map(branch => (
                                 <label key={branch.id} className={`flex items-center px-4 py-3 rounded-2xl border-2 cursor-pointer transition-all duration-300 ${formData.branchIds.includes(branch.id) ? 'bg-blue-50 border-blue-600 text-blue-900' : 'bg-gray-50 border-transparent text-gray-500 hover:border-gray-200'}`}>
                                    <input
                                      type="checkbox"
                                      checked={formData.branchIds.includes(branch.id)}
                                      onChange={(e) => {
                                        const newIds = e.target.checked
                                          ? [...formData.branchIds, branch.id]
                                          : formData.branchIds.filter(id => id !== branch.id);
                                        setFormData({ ...formData, branchIds: newIds });
                                      }}
                                      className="hidden"
                                    />
                                    <div className="flex flex-col min-w-0">
                                      <span className="text-[10px] font-black uppercase tracking-widest truncate">{branch.displayName}</span>
                                      <span className="text-[8px] font-bold text-zinc-400 uppercase tracking-tighter truncate">{branch.gym.displayName}</span>
                                    </div>
                                 </label>
                               ))}
                            </div>
                            <p className="text-[9px] font-black text-zinc-300 uppercase tracking-widest text-center italic">SELECT THE BRANCHES THIS MEMBER CAN ACCESS · NONE = NO ACCESS</p>
                         </div>
                      </div>

                      <div className="flex gap-3 px-6 py-5 border-t border-zinc-50 shrink-0">
                        <button
                          type="button"
                          onClick={() => setShowEditModal(false)}
                          className="flex-1 py-3.5 text-xs font-black text-gray-400 hover:text-gray-900 uppercase tracking-[0.2em] transition-colors"
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          disabled={isSubmitting}
                          className="flex-[2] py-3.5 bg-gray-900 hover:bg-black text-white rounded-2xl text-xs font-black uppercase tracking-[0.2em] transition-all shadow-xl shadow-gray-200 active:scale-95 disabled:opacity-50"
                        >
                          {isSubmitting ? 'Updating...' : 'Update'}
                        </button>
                      </div>
                   </form>
                </div>
              </div>
            )}

            {/* Soft Delete Confirmation Modal */}
            {showDeleteModal && selectedMember && (
              <div className="fixed inset-0 bg-black/70 backdrop-blur-xl flex items-center justify-center z-[80] p-4 animate-in fade-in duration-500">
                <div className="bg-white rounded-[48px] shadow-3xl w-full max-w-md overflow-hidden animate-in zoom-in-95 duration-500">
                   <div className="p-10 text-center">
                     <div className="w-24 h-24 bg-red-50 rounded-[32px] flex items-center justify-center mb-8 mx-auto ring-8 ring-red-500/5">
                        <svg className="w-10 h-10 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                     </div>
                     <h2 className="text-2xl font-black text-zinc-900 mb-2">Remove Member?</h2>
                     <p className="text-zinc-400 text-[10px] font-bold mb-8 uppercase tracking-widest px-4">
                        YOU ARE MARKING <span className="text-red-600">{selectedMember.name}</span> AS INACTIVE.
                     </p>

                     <div className="space-y-3">
                        <button
                          onClick={handleDeleteMember}
                          disabled={isSubmitting}
                          className="w-full py-4 bg-red-600 hover:bg-red-700 text-white rounded-2xl font-black uppercase tracking-widest transition-all transform active:scale-95 shadow-lg shadow-red-500/10 disabled:opacity-50"
                        >
                          {isSubmitting ? 'Processing...' : 'Make Inactive'}
                        </button>
                        <button
                          onClick={() => setShowDeleteModal(false)}
                          className="w-full py-4 bg-transparent text-zinc-400 hover:text-zinc-900 font-bold text-xs uppercase tracking-widest transition-colors"
                        >
                          Cancel
                        </button>
                     </div>
                   </div>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="p-1">
            {renderTabContent()}
          </div>
        )}
      </div>
    </div>
  );
};

export default TeamPage; 